import { html, TemplateResult } from 'lit';
import type { ImageRenderData } from '../utils/gallery.utils.js';
import { galleryImageTemplate } from './gallery-image.template.js';

// Treat ratios like '2/3' or '0.66' as numeric values for layout decisions.
function parseAspectRatio(aspectRatio?: string): number {
    if (!aspectRatio) return 1;

    if (aspectRatio.includes('/')) {
        const [width, height] = aspectRatio.split('/').map((value) => Number(value));
        if (Number.isFinite(width) && Number.isFinite(height) && height !== 0) {
            return width / height;
        }
    }

    const numericRatio = Number(aspectRatio);
    return Number.isFinite(numericRatio) && numericRatio > 0 ? numericRatio : 1;
}

/**
 * Helper function to apply orientation classes based on the loaded image's natural dimensions.
 * Exported for testing purposes but can be used as an onLoad callback for gallery images to ensure correct layout.
 * @param event Event
 * @returns void
 */
export function applyOrientationFromImageLoad(event: Event): void {
    const image = event.currentTarget as HTMLImageElement | null;
    if (!image) return;

    const container = image.closest<HTMLButtonElement>('.der-tm-roundtrip-gallery-overlay__grid-item');
    if (!container) return;

    const isPortrait = image.naturalWidth > 0 && image.naturalHeight > 0 && image.naturalWidth < image.naturalHeight;
    container.classList.toggle('der-tm-roundtrip-gallery-overlay__grid-item--portrait', isPortrait);
    container.classList.toggle('der-tm-roundtrip-gallery-overlay__grid-item--landscape', !isPortrait);
}

/**
 * Renders the gallery grid template.
 * @param images - Flat list of images to render
 * @param onSelect - Called with the selected index
 * @returns The gallery grid template result.
 */
export function galleryGridTemplate(images: ImageRenderData[], onSelect: (index: number) => void): TemplateResult {
    return html`
        <div class="der-tm-roundtrip-gallery-overlay__grid" role="list">
            ${images.map((image, index) => {
                const aspectRatioValue = parseAspectRatio(image.aspectRatio);
                return html`
                    <button
                        class=${`der-tm-roundtrip-gallery-overlay__grid-item ${
                            aspectRatioValue < 1
                                ? 'der-tm-roundtrip-gallery-overlay__grid-item--portrait'
                                : 'der-tm-roundtrip-gallery-overlay__grid-item--landscape'
                        }`}
                        type="button"
                        aria-label=${image.alt || 'Open image'}
                        @mousedown=${(event: Event) => event.preventDefault()}
                        @click=${(event: Event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onSelect(index);
                        }}
                    >
                        ${galleryImageTemplate(image, 'eager', applyOrientationFromImageLoad)}
                    </button>
                `;
            })}
        </div>
    `;
}
